import React from "react";
import { Box } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Card, { CardProps } from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import StatusChip, { TestRequestDisplayedStateStatus } from "../../../components/Chip/StatusChip";
import { tesaBlue4, tesaGrey1, tesaGrey6 } from "../../../styles/colors";
import UserChip from "../../../components/Chip/UserChip";
import { dashToSlash } from "../../../api/services/common/FormatDateService";
import CardContentItem from "./CardContentItem";
import RequestFlagIconList from "../../../components/RequestFlags/RequestFlagIconList";
import { TestRequestFlag } from "../../../components/RequestFlags/RequestFlag";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginBottom: theme.spacing(1),
      minWidth: "180px",
    },
    content: {
      backgroundColor: tesaGrey6,
      "&:hover": {
        backgroundColor: tesaBlue4,
        cursor: "pointer",
      },
    },
    statusChip: {
      paddingBottom: theme.spacing(1),
    },
  }),
);

interface TestRequestCardProps extends CardProps {
  permission: boolean;
  assignee?: string;
  customer: string;
  priority: string;
  pnsPotential: string;
  customerClassification: string;
  deadline: string;
  estimatedFinishingDate: string;
  flags: TestRequestFlag[];
  name: string;
  onCardClick: () => void;
  requester: string;
  status: TestRequestDisplayedStateStatus;
  submissionDate: string;
  testRequestId: string;
  handleMultiTab?: () => void;
  onPriorityChange?: (id: string, priority: string) => void;
}

const TestRequestCard = (props: TestRequestCardProps) => {
  const {
    permission: editPriority,
    assignee,
    customer,
    pnsPotential,
    customerClassification,
    deadline,
    estimatedFinishingDate,
    flags,
    name,
    onCardClick,
    requester,
    status,
    submissionDate,
    testRequestId,
    handleMultiTab,
    onPriorityChange,
  } = props;
  const classes = useStyles();

  const priorityItemTemplate = () => (
    <Box
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onPriorityChange?.(testRequestId, props.priority);
      }}
    >
      <CardContentItem title="Priority" text={props.priority || " "} />
    </Box>
  );

  return (
    <Card
      className={classes.root}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey) {
          if (handleMultiTab) {
            handleMultiTab();
            return;
          }
        }
        if (onCardClick) {
          onCardClick();
        }
      }}
      id={testRequestId}
    >
      <CardContent className={classes.content}>
        <CardContentItem title="ID" text={testRequestId} />
        <CardContentItem>
          <RequestFlagIconList flags={flags} gutterSpacing={1.5} />
        </CardContentItem>
        <CardContentItem text={name} header />
        {editPriority && priorityItemTemplate()}
        <CardContentItem title="Customer" text={customer} />
        <CardContentItem title="Customer Class" text={customerClassification} />
        <CardContentItem title="Requester" text={requester} />
        <CardContentItem title="PNS Potential" text={pnsPotential} />
        <CardContentItem title="Submission Date" text={dashToSlash(submissionDate)} />
        <CardContentItem
          text={`${deadline !== null ? dashToSlash(deadline) : ""}`}
          title="Deadline"
          warning={flags.includes("DEADLINE_MAY_NOT_BE_MET")}
        />
        <CardContentItem
          text={`${estimatedFinishingDate !== null ? dashToSlash(estimatedFinishingDate) : ""}`}
          title="EFD"
        />
        <StatusChip className={classes.statusChip} status={status} />
        <UserChip userName={assignee || "Unassigned"} />
      </CardContent>
    </Card>
  );
};

export default TestRequestCard;
